/**
 * The BOM consequences of choosing a catalog heater (issue 159).
 *
 * Every engine bills a generic heater row ("Bastuaggregat" / "Elaggregat").
 * With a model chosen, that row becomes the model — its name, its kW and its
 * own price rule — and the model's extras follow:
 *
 * - an integrated control means no separate styrpanel is bought;
 * - an outside-fired unit (eldas utifrån) passes through the wall it stands
 *   against, so that wall gets non-combustible board on both faces around
 *   the feed tunnel, and the tunnel is sealed with A1 stone wool.
 *
 * One function for every engine: the engines' heater rows are identical, and
 * a per-engine copy of this rewrite is exactly the drift the enrichment chain
 * exists to prevent.
 */
import type { MaterialRow } from "../../engines/types";
import {
	type HeaterModel,
	type HeaterSelection,
	heaterModelPriceRuleId,
	resolveHeaterModel,
} from "./heaterCatalog";

const HEATER_ROW_KEYS = new Set(["heater.wood", "heater.electric"]);

/** One 1220 × 1000 mm board. */
const FIRE_BOARD_AREA_M2 = 1.22;

/**
 * Boards for one face of the fire wall: the unit's width plus its side
 * distance on either hand, from the floor to a side distance above its top.
 * An estimate of the protected patch, not a substitute for the manual.
 */
export function fireBoardsPerFace(model: HeaterModel): number {
	const sideMm = model.clearanceMm.side ?? 0;
	const widthM = (model.bodyMm.width + 2 * sideMm) / 1000;
	const heightM = (model.bodyMm.height + sideMm) / 1000;
	return Math.ceil((widthM * heightM) / FIRE_BOARD_AREA_M2);
}

export function applyHeaterModelToMaterials(
	materials: MaterialRow[],
	selection: HeaterSelection,
): MaterialRow[] {
	const model = resolveHeaterModel(selection);
	if (!model) return materials;

	const rows = materials
		.filter(
			(row) =>
				!(
					model.integratedControl && row.materialKey === "heater.control-panel"
				),
		)
		.map((row) =>
			row.materialKey && HEATER_ROW_KEYS.has(row.materialKey)
				? {
						...row,
						material: model.name,
						dimension: `${model.powerKw} kW`,
						priceRuleId: heaterModelPriceRuleId(model),
					}
				: row,
		);

	if (model.firing !== "outside") return rows;

	return [
		...rows,
		{
			material: "Brandskiva kalciumsilikat",
			dimension: "1220×1000×50 mm, båda sidor om genomföringen",
			quantity: `${fireBoardsPerFace(model) * 2} st`,
			phase: "systems",
			priceRuleId: "prices.brandskivaKalciumsilikat",
			materialKey: "heater.through-wall.fire-board",
		},
		{
			material: "Brandtätning eldningstunnel",
			dimension: "Obrännbar stenull A1 runt tunneln",
			quantity: "1 st",
			phase: "systems",
			priceRuleId: "prices.brandtatning",
			materialKey: "heater.through-wall.seal",
		},
	];
}
